import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedOption {
  value: string;
  label: string;
  /** Tints the selected pill — hot/warm/cold and high/normal/low read better in colour. */
  tone?: 'brand' | 'danger' | 'warn' | 'ok' | 'info';
}

const SELECTED_TONE: Record<NonNullable<SegmentedOption['tone']>, string> = {
  brand: 'bg-brand text-brand-ink',
  danger: 'bg-danger text-white',
  warn: 'bg-warn text-white',
  ok: 'bg-ok text-white',
  info: 'bg-info text-white',
};

interface Props {
  options: readonly SegmentedOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** Lets a chosen value be un-chosen, for the optional ones like lead rating. */
  clearable?: boolean;
  id?: string;
  /** Required when the group has no visible <label> — a filter row usually does not. */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  name: string;
}

/**
 * A radio group that looks like a row of pills.
 *
 * Built on real radio inputs rather than buttons: arrow keys move between
 * options, the group takes one tab stop, and a screen reader announces
 * "2 of 3" — none of which a div-with-onClick gives you.
 */
export const Segmented = forwardRef<HTMLInputElement, Props>(function Segmented(
  { options, value, onValueChange, clearable, id, name, ...aria },
  ref,
) {
  return (
    <div
      role="radiogroup"
      id={id}
      aria-label={aria['aria-label']}
      aria-invalid={aria['aria-invalid']}
      aria-describedby={aria['aria-describedby']}
      className="inline-flex flex-wrap gap-1 rounded-[var(--radius-control)] bg-surface-2 p-1"
    >
      {options.map((o, i) => {
        const selected = value === o.value;
        return (
          <label
            key={o.value}
            className={cn(
              'cursor-pointer rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-sm font-semibold transition-colors',
              'focus-within:ring-2 focus-within:ring-brand/40',
              selected ? SELECTED_TONE[o.tone ?? 'brand'] : 'text-muted hover:text-ink',
            )}
          >
            <input
              ref={i === 0 ? ref : undefined}
              type="radio"
              name={name}
              value={o.value}
              checked={selected}
              className="sr-only"
              onChange={() => onValueChange(o.value)}
              // Clicking the chosen pill clears it, so an optional rating can
              // be un-set without a separate "none" option cluttering the row.
              onClick={() => clearable && selected && onValueChange('')}
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
});
